/**
 * Product Module
 * 产品模块
 * 
 * @description 处理产品页面功能：变体选择、图片画廊等
 * @version 1.0.0
 */

const ProductModule = (function() {
  'use strict';

  /**
   * 初始化产品变体选择器
   * @param {Element} container - 产品容器元素
   */
  function initVariantSelector(container) {
    const selects = container.querySelectorAll('[data-variant-select]');
    const variantIdInput = container.querySelector('[name="id"]');
    const productJson = container.querySelector('[data-product-json]');

    if (!productJson || !variantIdInput) return;

    const product = JSON.parse(productJson.textContent);

    selects.forEach(select => {
      select.addEventListener('change', () => {
        updateVariant(product, container);
      });
    });

    // 初始化时更新一次
    updateVariant(product, container);
  }

  /**
   * 更新选中的变体
   * @param {Object} product - 产品数据
   * @param {Element} container - 产品容器
   */
  function updateVariant(product, container) {
    const selects = container.querySelectorAll('[data-variant-select]');
    const selectedOptions = Array.from(selects).map(select => select.value);

    // 查找匹配的变体
    const variant = product.variants.find(v => {
      return v.options.every((option, index) => option === selectedOptions[index]);
    });

    if (variant) {
      // 更新隐藏的变体 ID 输入框
      const variantIdInput = container.querySelector('[name="id"]');
      if (variantIdInput) {
        variantIdInput.value = variant.id;
      }

      // 更新价格（如果用户有权限查看）
      updatePrice(variant, container);

      // 更新库存状态
      updateAvailability(variant, container);

      // 更新图片
      updateImage(variant, container);

      // 更新 URL
      updateURL(variant);

      // 触发变体变化事件
      container.dispatchEvent(new CustomEvent('product:variantChanged', {
        detail: { variant }
      }));
    }
  }

  /**
   * 更新价格显示
   * @param {Object} variant - 变体数据
   * @param {Element} container - 产品容器
   */
  function updatePrice(variant, container) {
    const priceElement = container.querySelector('[data-product-price]');
    const comparePriceElement = container.querySelector('[data-product-compare-price]');

    if (!priceElement) return;

    // 检查用户是否为 dealer
    const isDealer = DealerThemeUtils.isDealerUser();
    const isLoggedIn = DealerThemeUtils.isLoggedIn();

    if (isDealer) {
      // Dealer 用户显示实际价格
      const price = DealerThemeUtils.formatMoney(variant.price, window.Shopify.money_format);
      priceElement.textContent = price;
      priceElement.classList.remove('price--hidden');

      // 显示对比价格
      if (comparePriceElement && variant.compare_at_price && variant.compare_at_price > variant.price) {
        const comparePrice = DealerThemeUtils.formatMoney(variant.compare_at_price, window.Shopify.money_format);
        comparePriceElement.textContent = comparePrice;
        comparePriceElement.style.display = '';
      } else if (comparePriceElement) {
        comparePriceElement.style.display = 'none';
      }
    } else {
      // 非 dealer 用户显示占位符
      const placeholder = window.themeSettings?.pricePlaceholder || '?';
      priceElement.textContent = placeholder;
      priceElement.classList.add('price--hidden');
      
      if (comparePriceElement) {
        comparePriceElement.style.display = 'none';
      }
    }
  }

  /**
   * 更新库存状态
   * @param {Object} variant - 变体数据
   * @param {Element} container - 产品容器
   */
  function updateAvailability(variant, container) {
    const availabilityElement = container.querySelector('[data-product-availability]');
    const addToCartButton = container.querySelector('[data-add-to-cart-button]');

    if (variant.available) {
      if (availabilityElement) {
        availabilityElement.textContent = '有货';
        availabilityElement.classList.add('in-stock');
        availabilityElement.classList.remove('out-of-stock');
      }
      
      if (addToCartButton) {
        addToCartButton.disabled = false;
        addToCartButton.textContent = addToCartButton.dataset.defaultText || '加入购物车';
      }
    } else {
      if (availabilityElement) {
        availabilityElement.textContent = '缺货';
        availabilityElement.classList.add('out-of-stock');
        availabilityElement.classList.remove('in-stock');
      }
      
      if (addToCartButton) {
        addToCartButton.disabled = true;
        addToCartButton.textContent = '缺货';
      }
    }
  }

  /**
   * 更新产品图片
   * @param {Object} variant - 变体数据
   * @param {Element} container - 产品容器
   */
  function updateImage(variant, container) {
    if (!variant.featured_image) return;

    const mainImage = container.querySelector('[data-product-main-image]');
    if (mainImage) {
      mainImage.src = getHighResUrl(variant.featured_image.src, 1200);
      mainImage.alt = variant.featured_image.alt || variant.name;
    }
  }

  /**
   * 获取指定宽度的高清图片 URL
   * @param {string} src - 原始图片 URL
   * @param {number} width - 目标宽度
   * @returns {string} 高清图片 URL
   */
  function getHighResUrl(src, width) {
    if (!src) return src;
    try {
      const url = new URL(src, window.location.origin);
      if (url.searchParams.has('width')) {
        url.searchParams.set('width', width);
        return url.toString();
      }
      if (url.pathname.match(/_\d+x(\.|$)/)) {
        url.pathname = url.pathname.replace(/_\d+x/, `_${width}x`);
        return url.toString();
      }
      url.searchParams.set('width', width);
      return url.toString();
    } catch (e) {
      if (src.includes('width=')) return src.replace(/width=\d+/, `width=${width}`);
      return src;
    }
  }

  /**
   * 更新浏览器 URL
   * @param {Object} variant - 变体数据
   */
  function updateURL(variant) {
    if (!variant || !window.history.replaceState) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * 初始化产品图片画廊
   * @param {Element} container - 产品容器
   */
  function initImageGallery(container) {
    const thumbnails = container.querySelectorAll('[data-product-thumbnail]');
    const mainImage = container.querySelector('[data-product-main-image]');

    if (!mainImage) return;

    thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        
        const imageUrl = thumbnail.dataset.imageUrl || thumbnail.dataset.fullUrl;
        const imageAlt = thumbnail.dataset.imageAlt;

        if (imageUrl) {
          mainImage.src = imageUrl;
          mainImage.alt = imageAlt || '';
        } else {
          // 回退: 从缩略图 img 获取高清 URL
          const thumbImg = thumbnail.querySelector('img');
          if (thumbImg) {
            mainImage.src = getHighResUrl(thumbImg.src, 1200);
            mainImage.alt = thumbImg.alt || '';
          }
        }

        // 更新活动缩略图
        thumbnails.forEach(t => t.classList.remove('is-active'));
        thumbnail.classList.add('is-active');
      });
    });
  }

  /**
   * 初始化数量选择器
   * @param {Element} container - 产品容器
   */
  function initQuantitySelector(container) {
    const quantityInputs = container.querySelectorAll('[data-quantity-input]');

    quantityInputs.forEach(input => {
      const decreaseBtn = input.parentElement.querySelector('[data-quantity-decrease]');
      const increaseBtn = input.parentElement.querySelector('[data-quantity-increase]');

      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', () => {
          const currentValue = parseInt(input.value) || 1;
          const minValue = parseInt(input.min) || 1;
          
          if (currentValue > minValue) {
            input.value = currentValue - 1;
            input.dispatchEvent(new Event('change'));
          }
        });
      }

      if (increaseBtn) {
        increaseBtn.addEventListener('click', () => {
          const currentValue = parseInt(input.value) || 1;
          const maxValue = parseInt(input.max) || 999;
          
          if (currentValue < maxValue) {
            input.value = currentValue + 1;
            input.dispatchEvent(new Event('change'));
          }
        });
      }

      // 验证输入
      input.addEventListener('change', () => {
        let value = parseInt(input.value);
        const minValue = parseInt(input.min) || 1;
        const maxValue = parseInt(input.max) || 999;

        if (isNaN(value) || value < minValue) {
          value = minValue;
        } else if (value > maxValue) {
          value = maxValue;
        }

        input.value = value;
      });
    });
  }

  /**
   * 初始化产品模块
   */
  function init() {
    const productContainers = document.querySelectorAll('[data-product-container]');

    productContainers.forEach(container => {
      initVariantSelector(container);
      initImageGallery(container);
      initQuantitySelector(container);
    });

    console.log('📦 Product module initialized');
  }

  // 返回公开 API
  return {
    init,
    initVariantSelector,
    initImageGallery,
    initQuantitySelector
  };
})();

// DOM 加载完成后初始化
document.addEventListener('DOMContentLoaded', () => {
  ProductModule.init();
});

// 暴露到全局
window.ProductModule = ProductModule;

